// Admin → Company settings (COMPANY-SETTINGS-ADMIN PART 1). A SINGLETON FORM, not a list:
// load, edit, save, one toast. Closest existing shape: TasksSettingsManager.
//
// The whole Admin page is gated by RequireRole('admin') (App.tsx) and the server enforces
// requireRole('admin') on GET /api/company-settings/admin and PATCH /api/company-settings, so
// this is a thin form over those two endpoints. All authority stays server-side — in
// particular the field whitelist, which is what makes the held authority fields unwritable
// (company-settings.schemas.ts). This screen simply does not offer them.
//
// FIELDS ARE GROUPED AS AN OPERATOR THINKS ABOUT THEM — Booking defaults, Board appearance,
// Units, Operations — not in model order.

import { useEffect, useState, type CSSProperties } from 'react';
import { C, F, card, Btn, ErrorText } from './adminUi';
import * as api from '../../lib/companySettings';
import type { AdminBase } from '../../lib/admin';
import { formatSessionTimes } from '../../lib/sessionTimes';

const inputStyle: CSSProperties = { fontFamily: F, fontSize: 13, padding: '6px 8px', border: `1px solid ${C.borderMed}`, borderRadius: 6, background: '#fff', color: C.text, width: 130 };
const selectStyle: CSSProperties = { ...inputStyle, width: 180 };
const labelStyle: CSSProperties = { fontWeight: 600, fontSize: 13, color: C.text, display: 'block', marginBottom: 2 };
const hintStyle: CSSProperties = { fontSize: 12, color: C.textDim, marginBottom: 8 };
const sectionStyle: CSSProperties = { fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.4, color: C.textMuted, margin: '18px 0 10px', fontWeight: 700 };

const nonNegInt = (v: string): number => { const n = Math.floor(Number(v)); return Number.isFinite(n) && n > 0 ? n : 0; };

export default function CompanySettingsManager({ bases, setToast }: { bases: AdminBase[]; setToast: (s: string | null) => void }) {
  const [s, setS] = useState<api.AdminCompanySettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.getAdminCompanySettings().then((d) => { if (d) setS(d); else setErr('Could not load company settings.'); });
  }, []);

  if (err && !s) return <div style={card}><ErrorText>{err}</ErrorText></div>;
  if (!s) return <div style={{ ...card, color: C.textMuted, fontSize: 13 }}>Loading…</div>;

  const set = <K extends keyof api.AdminCompanySettings>(k: K, v: api.AdminCompanySettings[K]) => setS({ ...s, [k]: v });

  // ⚠ Mirrors the server rule (company-settings.service assertChannelsDiffer). The SERVER is the
  // authority — this only stops the operator submitting a save that is going to be refused.
  const channelsClash = s.chip_top_bar_channel === s.chip_fill_channel;

  async function save() {
    setBusy(true); setErr('');
    // Explicit field list, never a spread of state — the same discipline the endpoint enforces.
    const r = await api.patchCompanySettings({
      default_booking_mins: s!.default_booking_mins,
      default_buffer_pre_mins: s!.default_buffer_pre_mins,
      default_buffer_post_mins: s!.default_buffer_post_mins,
      chip_top_bar_channel: s!.chip_top_bar_channel,
      chip_fill_channel: s!.chip_fill_channel,
      unit_weight: s!.unit_weight, unit_volume: s!.unit_volume,
      unit_fuel_avgas: s!.unit_fuel_avgas, unit_fuel_jet: s!.unit_fuel_jet,
      unit_temp: s!.unit_temp, unit_distance: s!.unit_distance,
      unit_altitude: s!.unit_altitude, unit_speed: s!.unit_speed,
      mr_escalation_hours: s!.mr_escalation_hours,
      fuel_discrepancy_threshold: s!.fuel_discrepancy_threshold,
      tr_photo_required: s!.tr_photo_required,
      mr_photo_required: s!.mr_photo_required,
    });
    setBusy(false);
    if (!r.ok) { setErr(r.error || 'Could not save company settings.'); return; }
    if (r.data) setS(r.data); // reflect what the server actually stored
    setToast('Company settings saved.');
  }

  const numberRow = (
    key: 'default_booking_mins' | 'default_buffer_pre_mins' | 'default_buffer_post_mins' | 'mr_escalation_hours',
    label: string, hint: string,
  ) => (
    <div style={{ marginBottom: 14 }}>
      <label style={labelStyle}>{label}</label>
      <div style={hintStyle}>{hint}</div>
      <input type="number" min={0} value={s![key]} onChange={(e) => set(key, nonNegInt(e.target.value))} style={inputStyle} />
    </div>
  );

  const boolRow = (key: 'tr_photo_required' | 'mr_photo_required', label: string, hint: string) => (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600, fontSize: 13, color: C.text }}>
        <input type="checkbox" checked={s![key]} onChange={(e) => set(key, e.target.checked)} style={{ accentColor: C.accent }} />
        {label}
      </label>
      <div style={{ ...hintStyle, marginLeft: 24, marginBottom: 0 }}>{hint}</div>
    </div>
  );

  const channelRow = (key: 'chip_top_bar_channel' | 'chip_fill_channel', label: string, hint: string) => (
    <div style={{ marginBottom: 14 }}>
      <label style={labelStyle}>{label}</label>
      <div style={hintStyle}>{hint}</div>
      <select value={s![key]} onChange={(e) => set(key, e.target.value as api.ChipChannel)} style={selectStyle}>
        {api.CHIP_CHANNEL_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  const unitRow = (key: keyof typeof api.UNIT_OPTIONS) => (
    <div key={key}>
      <label style={{ ...labelStyle, fontSize: 12, color: C.textDim }}>{api.UNIT_LABELS[key]}</label>
      <select value={String(s![key as keyof api.AdminCompanySettings])}
        onChange={(e) => set(key as keyof api.AdminCompanySettings, e.target.value as never)} style={{ ...selectStyle, width: '100%' }}>
        {api.UNIT_OPTIONS[key].map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );

  const activeBases = bases.filter((b) => b.is_active);

  return (
    <div style={card}>
      <h1 style={{ fontSize: 17, margin: '0 0 4px' }}>Company settings</h1>
      <div style={{ fontSize: 12.5, color: C.textDim, marginBottom: 6 }}>
        Company-wide defaults and preferences. Changes take effect immediately — no redeploy.
      </div>

      <div style={sectionStyle}>Booking defaults</div>
      {numberRow('default_booking_mins', 'Standard booking length (minutes)',
        'Seeds the length of a new booking. A starting point for ease of entry — any booking can still be set to any length.')}
      {/* ⚠ Required by the brief, and non-obvious: the buffers are read at CREATION only. */}
      <div style={{ fontSize: 12, color: C.textDim, background: C.surfaceAlt, border: `1px solid ${C.border}`, borderRadius: 6, padding: '8px 10px', marginBottom: 12 }}>
        The two buffers below apply to <b>new bookings only</b>. Bookings that already exist keep the
        buffer they were created with, so the board will show both conventions side by side for a while.
      </div>
      {numberRow('default_buffer_pre_mins', 'Buffer before a booking (minutes)', 'Turnaround time reserved ahead of a new booking.')}
      {numberRow('default_buffer_post_mins', 'Buffer after a booking (minutes)', 'Turnaround time reserved after a new booking.')}

      <div style={sectionStyle}>Board appearance</div>
      <div style={{ ...hintStyle, marginBottom: 12 }}>
        What each colour on an ops-board chip means. The left-hand bar always shows confirmation
        status and is not configurable. <b>The two below must show different things.</b>
      </div>
      {channelRow('chip_top_bar_channel', 'Chip top bar shows', 'The thin bar across the top of each chip.')}
      {channelRow('chip_fill_channel', 'Chip background shows', 'The background tint of the chip.')}
      {channelsClash && <ErrorText>The top bar and the background must show different things — pick two of: booking type, instructor, base.</ErrorText>}

      <div style={sectionStyle}>Units</div>
      <div style={{ ...hintStyle, marginBottom: 12 }}>How quantities are displayed across the app.</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))', gap: 10, marginBottom: 6 }}>
        {(Object.keys(api.UNIT_OPTIONS) as (keyof typeof api.UNIT_OPTIONS)[]).map(unitRow)}
      </div>

      <div style={sectionStyle}>Operations</div>
      {numberRow('mr_escalation_hours', 'Maintenance request escalation (hours)', 'How long an unactioned maintenance request waits before it is escalated.')}
      <div style={{ marginBottom: 14 }}>
        <label style={labelStyle}>Fuel discrepancy threshold</label>
        <div style={hintStyle}>A recorded fuel figure differing from the expected figure by more than this raises a discrepancy.</div>
        <input type="number" min={0} step={0.01} value={s.fuel_discrepancy_threshold}
          onChange={(e) => { const n = Number(e.target.value); set('fuel_discrepancy_threshold', Number.isFinite(n) && n > 0 ? n : 0); }}
          style={inputStyle} />
      </div>
      {boolRow('tr_photo_required', 'Photo required on a trip record', 'A trip record cannot be completed without a photo.')}
      {boolRow('mr_photo_required', 'Photo required on a maintenance request', 'A maintenance request cannot be raised without a photo.')}

      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10 }}>
        <Btn kind="primary" disabled={busy || channelsClash} onClick={save}>Save settings</Btn>
        <ErrorText>{err}</ErrorText>
      </div>

      {/* ⚠ READ-ONLY HERE ON PURPOSE. Session times live on Base, and a base is edited in the
          Bases tab. One record, one place to edit it — this is the summary, not a second form. */}
      <div style={sectionStyle}>Session start times</div>
      <div style={{ ...hintStyle, marginBottom: 10 }}>
        Suggested start times, set <b>per base</b> because locations change seasonally. Edit them in
        the <b>Bases</b> tab. Sessions run for the standard booking length above. They are quick-pick
        suggestions on the booking forms — a booking may still start at any time.
      </div>
      {activeBases.length === 0
        ? <div style={{ fontSize: 12.5, color: C.textMuted, fontStyle: 'italic' }}>No active bases.</div>
        : activeBases.map((b) => {
          const times = formatSessionTimes(b.session_start_times);
          return (
            <div key={b.id} style={{ display: 'flex', gap: 12, alignItems: 'baseline', padding: '6px 0', borderBottom: `1px solid ${C.border}`, fontSize: 13 }}>
              <div style={{ fontWeight: 600, minWidth: 180 }}>{b.name} <span style={{ color: C.textDim, fontWeight: 400 }}>({b.short_code})</span></div>
              <div style={{ color: times ? C.text : C.textMuted, fontStyle: times ? 'normal' : 'italic' }}>{times || 'none set'}</div>
              <div style={{ marginLeft: 'auto', color: C.textDim, fontSize: 12 }}>
                {b.operating_hours_start || b.operating_hours_end
                  ? `open ${b.operating_hours_start ?? '—'}–${b.operating_hours_end ?? '—'}`
                  : 'no operating hours recorded'}
              </div>
            </div>
          );
        })}
    </div>
  );
}
